import { stateKey } from '../../../../app/state-key.js';
import { deepEqualAnyOrder } from '../../../../utils/compare-utils.js';
import { validateInitialState } from '../../../../utils/validate-payload.js';
import { buildController } from '../../../controller/headless-controller.js';
import { initialStateSchema, } from '../../../url-manager/headless-url-manager.js';
/**
 * @internal
 * Creates a `UrlManager` sub-controller instance.
 *
 * @param engine - The headless commerce engine.
 * @param props - The configurable `UrlManager` properties.
 * @returns A `UrlManager` sub-controller instance.
 */
export function buildCoreUrlManager(engine, props) {
    let lastRequestId;
    function updateLastRequestId() {
        lastRequestId = props.requestIdSelector(engine[stateKey]);
    }
    function hasRequestIdChanged() {
        return lastRequestId !== props.requestIdSelector(engine[stateKey]);
    }
    validateInitialState(engine, initialStateSchema, props.initialState, 'buildUrlManager');
    const controller = buildController(engine);
    let previousFragment = props.initialState.fragment;
    updateLastRequestId();
    const parameterManager = props.parameterManagerBuilder(engine, {
        initialState: {
            parameters: props.serializer.deserialize(previousFragment),
        },
        excludeDefaultParameters: props.excludeDefaultParameters ?? false,
    });
    return {
        ...controller,
        subscribe(listener) {
            const strictListener = () => {
                const newFragment = this.state.fragment;
                if (!areFragmentsEquivalent(props.serializer.deserialize, previousFragment, newFragment) &&
                    hasRequestIdChanged()) {
                    previousFragment = newFragment;
                    listener();
                }
                updateLastRequestId();
            };
            strictListener();
            return engine.subscribe(strictListener);
        },
        get state() {
            return {
                fragment: props.serializer.serialize(parameterManager.state.parameters),
            };
        },
        synchronize(fragment) {
            previousFragment = fragment;
            const parameters = props.serializer.deserialize(fragment);
            parameterManager.synchronize(parameters);
        },
    };
}
function areFragmentsEquivalent(deserialize, fragment1, fragment2) {
    if (fragment1 === fragment2) {
        return true;
    }
    const params1 = deserialize(fragment1);
    const params2 = deserialize(fragment2);
    return deepEqualAnyOrder(params1, params2);
}
